/**
 * Pure math for the /baggers ranking page: trailing price multiples computed
 * from daily candle closes. Kept free of fetching/React so the window-clamp
 * and edge cases stay unit-testable.
 */

/** One daily bar reduced to what the ranking needs. `t` = open time (ms). */
export type DailyBar = { t: number; c: number };

export type BaggerStats = {
	/** markPrice ÷ close at the window start ("what holding it returned"). */
	trailing: number;
	/** markPrice ÷ lowest close inside the window ("up Nx from the low"). */
	fromLow: number;
	/** True when the asset listed after the window start (HIP-3 markets). */
	clamped: boolean;
};

export const BAGGER_WINDOWS = {
	"30d": 30,
	"90d": 90,
	"52w": 364,
} as const;

export type BaggerWindowKey = keyof typeof BAGGER_WINDOWS;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Compute return multiples over a trailing window. `bars` must be ascending by
 * time (Hyperliquid candleSnapshot order). Uses daily closes, not wick lows —
 * a flash-crash wick would otherwise dominate `fromLow`. Returns null when the
 * market has no bar inside the window (stale/delisted) or inputs are unusable.
 */
export function computeBaggerStats(
	bars: readonly DailyBar[],
	markPrice: number,
	windowDays: number,
	nowMs: number,
): BaggerStats | null {
	if (bars.length === 0 || !(markPrice > 0)) return null;
	const windowStart = nowMs - windowDays * DAY_MS;
	const inWindow = bars.filter((bar) => bar.t >= windowStart);
	if (inWindow.length === 0) return null;

	const baseline = inWindow[0].c;
	let low = Number.POSITIVE_INFINITY;
	for (const bar of inWindow) {
		if (bar.c < low) low = bar.c;
	}
	if (!(baseline > 0) || !(low > 0)) return null;

	return {
		trailing: markPrice / baseline,
		fromLow: markPrice / low,
		clamped: bars[0].t > windowStart,
	};
}

/**
 * Equity multiple of a leveraged LONG opened at the window start: leverage
 * applies to the price CHANGE, not the whole price — 5x on a 2x price move is
 * 1 + 5×(2−1) = 6x, not 10x. Clamped at 0 (equity can't go below zero).
 * At leverage 1 this is the price multiple itself.
 */
export function leveredMultiple(
	priceMultiple: number,
	leverage: number,
): number {
	return Math.max(0, 1 + leverage * (priceMultiple - 1));
}

/**
 * Whether a leveraged LONG opened at the window start would have been
 * liquidated before now. Worst drawdown since entry = lowest close ÷ entry
 * close = `trailing / fromLow` (both already computed from the same series);
 * a long dies when that reaches 1 − 1/leverage. Daily closes only, so
 * intraday-wick liquidations are underestimated.
 */
export function isLiquidated(
	stats: Pick<BaggerStats, "trailing" | "fromLow">,
	leverage: number,
): boolean {
	if (leverage <= 1) return false;
	const worstRatio = stats.trailing / stats.fromLow;
	return worstRatio <= 1 - 1 / leverage;
}

/**
 * Windowed closes downsampled to at most `maxPoints`, for row sparklines.
 * Keeps the first and last close so the line's endpoints stay truthful.
 */
export function buildTrend(
	bars: readonly DailyBar[],
	windowDays: number,
	nowMs: number,
	maxPoints = 40,
): number[] {
	const windowStart = nowMs - windowDays * DAY_MS;
	const closes = bars.filter((bar) => bar.t >= windowStart).map((bar) => bar.c);
	if (closes.length <= maxPoints) return closes;
	const step = (closes.length - 1) / (maxPoints - 1);
	return Array.from(
		{ length: maxPoints },
		(_, i) => closes[Math.round(i * step)],
	);
}
